// FILE: src/components/admin/AuditLogTable.jsx
// Audit-log rows as a table (WP-7, MTO 11.1, matrix 5.1 / 5.8), shared by
// the audit-log page and a student's Activity page: when, whose account,
// what happened in words with its outcome as icon + words, and the details
// as "label: value" pairs (never JSON). Paging is the server's
// ({ totalCount, page, limit }); ServerPagination drives it.
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Link, Stack, TableBody, TableCell, TableHead, TablePagination, TableRow, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { StatusChip } from "../ui";
import { ResponsiveTable } from "./AdminBits";
import { describeAuditDetails, formatDateTime, personName } from "./format";
import { PhoneCard, PhoneList, useIsPhone } from "./PhoneList";
import { auditActionLabel, auditOutcomeLabel } from "../../i18n/labels";

const OUTCOME_STYLE = {
  SUCCESS: { tone: "success", Icon: CheckCircleOutlineIcon },
  FAILURE: { tone: "error", Icon: ErrorOutlineIcon },
  INFO: { tone: "info", Icon: InfoOutlinedIcon }
};

export function OutcomeChip({ outcome }) {
  const style = OUTCOME_STYLE[outcome];
  if (!style) return null;
  const { tone, Icon } = style;
  return <StatusChip tone={tone} icon={<Icon />} label={auditOutcomeLabel(outcome)} />;
}

export function AuditDetails({ log }) {
  const items = describeAuditDetails(log);
  if (!items.length) return <Typography sx={{ color: "text.secondary" }}>No extra details</Typography>;
  return (
    <Box
      component="dl"
      sx={{ m: 0, display: "grid", gridTemplateColumns: "max-content minmax(0, 1fr)", columnGap: 1.5, rowGap: 0.5 }}
    >
      {items.map((it, i) => (
        <React.Fragment key={`${it.label}-${i}`}>
          <Typography component="dt" sx={{ color: "text.secondary", fontWeight: 600 }}>
            {it.label}:
          </Typography>
          <Typography component="dd" sx={{ m: 0, overflowWrap: "anywhere" }}>
            {it.value}
          </Typography>
        </React.Fragment>
      ))}
    </Box>
  );
}

/** Whose row this is: the account (linked to its page) or the email that was tried. */
function Account({ log }) {
  const owner = log.userId && typeof log.userId === "object" ? log.userId : null;
  if (owner?._id) {
    const name = personName(owner, owner.email || "Account");
    return (
      <Box>
        <Link component={RouterLink} to={`/admin/users/${owner._id}`} sx={{ fontWeight: 600 }}>
          {name}
        </Link>
        {owner.email && name !== owner.email && (
          <Typography sx={{ color: "text.secondary", overflowWrap: "anywhere" }}>{owner.email}</Typography>
        )}
      </Box>
    );
  }
  if (owner?.email || log.email) return <Typography sx={{ overflowWrap: "anywhere" }}>{owner?.email || log.email}</Typography>;
  return <Typography sx={{ color: "text.secondary" }}>No account</Typography>;
}

/**
 * logs: the rows of GET /api/admin/audit-logs. `showAccount` = false on a
 * student's own Activity page (every row is theirs).
 */
export default function AuditLogTable({ logs, label = "Audit log entries", showAccount = true }) {
  const phone = useIsPhone();
  if (phone) {
    return (
      <PhoneList label={label}>
        {logs.map((log, i) => (
          <PhoneCard key={log._id || i}>
            <Typography sx={{ color: "text.secondary" }}>{formatDateTime(log.createdAt)}</Typography>
            <Stack direction="row" sx={{ alignItems: "center", flexWrap: "wrap", gap: 1 }}>
              <Typography component="span" sx={{ fontWeight: 650 }}>
                {auditActionLabel(log.action)}
              </Typography>
              <OutcomeChip outcome={log.outcome} />
            </Stack>
            {showAccount && <Account log={log} />}
            <AuditDetails log={log} />
          </PhoneCard>
        ))}
      </PhoneList>
    );
  }
  return (
    <ResponsiveTable label={label} minWidth={showAccount ? 940 : 760}>
      <TableHead>
        <TableRow>
          <TableCell component="th" scope="col">
            When
          </TableCell>
          {showAccount && (
            <TableCell component="th" scope="col">
              Account
            </TableCell>
          )}
          <TableCell component="th" scope="col">
            What happened
          </TableCell>
          <TableCell component="th" scope="col">
            Details
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {logs.map((log, i) => (
          <TableRow key={log._id || i} sx={{ verticalAlign: "top" }}>
            <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDateTime(log.createdAt)}</TableCell>
            {showAccount && (
              <TableCell sx={{ maxWidth: 260 }}>
                <Account log={log} />
              </TableCell>
            )}
            <TableCell component="th" scope="row">
              <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
                <Typography component="span" sx={{ fontWeight: 600 }}>
                  {auditActionLabel(log.action)}
                </Typography>
                <OutcomeChip outcome={log.outcome} />
              </Stack>
            </TableCell>
            <TableCell>
              <AuditDetails log={log} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </ResponsiveTable>
  );
}

/** Lets the paging bar wrap on a phone so the next/previous buttons stay on screen. */
export const PAGINATION_SX = {
  mt: 1,
  overflow: "visible",
  "& .MuiTablePagination-toolbar": { flexWrap: "wrap", justifyContent: "flex-end", rowGap: 1, columnGap: 1, px: 0 },
  "& .MuiTablePagination-spacer": { display: { xs: "none", sm: "block" } },
  "& .MuiTablePagination-actions": { ml: 1 }
};

/** Server paging: page is 1-based; changing the page size goes back to page 1. */
export function ServerPagination({ totalCount, page, limit, onPage, onLimit, options = [25, 50, 100] }) {
  return (
    <TablePagination
      component="div"
      count={Number(totalCount) || 0}
      page={Math.max(0, (Number(page) || 1) - 1)}
      onPageChange={(_, p) => onPage(p + 1)}
      rowsPerPage={Number(limit) || options[0]}
      onRowsPerPageChange={(e) => onLimit(parseInt(e.target.value, 10))}
      rowsPerPageOptions={options.length > 1 ? options : []}
      labelRowsPerPage="Rows per page"
      labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count}`}
      getItemAriaLabel={(type) => (type === "next" ? "Next page" : type === "previous" ? "Previous page" : `Go to ${type} page`)}
      sx={PAGINATION_SX}
    />
  );
}
